import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { Paged } from "src/app/common/models/paged.model";
import { AbstractService } from "src/app/common/services/abstract.service";
import { TimedCache } from "src/app/common/tools/TimedCache";
import { MediaCollection } from "src/app/modules/speech2text/models/media-collection.model";
import { Speech2TextType } from "src/app/modules/speech2text/models/speech2text.model";

const API_PATH = "/api/internal/collections";

@Injectable({
  providedIn: "root",
})
export class CollectionService extends AbstractService {
  private readonly httpClient = inject(HttpClient);

  private readonly collectionsCache = {
    AUDIO: new TimedCache(
      60 * 60 * 1000,
      async () => await firstValueFrom(this.httpClient.get<MediaCollection[]>(`${API_PATH}/subscribed/AUDIO`)),
    ),
    VIDEO: new TimedCache(
      60 * 60 * 1000,
      async () => await firstValueFrom(this.httpClient.get<MediaCollection[]>(`${API_PATH}/subscribed/VIDEO`)),
    ),
  };

  async findSubscribed(speech2TextType: Speech2TextType): Promise<MediaCollection[]> {
    return await this.collectionsCache[speech2TextType].getValue();
  }

  async addCollection(mediaCollection: MediaCollection): Promise<void> {
    this.collectionsCache[mediaCollection.speech2TextType].clear();
    await firstValueFrom(this.httpClient.post(`${API_PATH}/add`, mediaCollection));
  }

  async updateCollection(mediaCollection: MediaCollection, automatic: boolean): Promise<void> {
    this.collectionsCache[mediaCollection.speech2TextType].clear();
    const params = new HttpParams().appendAll({ id: mediaCollection.id, automatic });
    await firstValueFrom(this.httpClient.put(`${API_PATH}/update`, null, { params }));
  }

  async updateAutomaticEmbeddingsCreation(
    mediaCollection: MediaCollection,
    automaticEmbeddingsCreation: boolean,
  ): Promise<void> {
    this.collectionsCache[mediaCollection.speech2TextType].clear();
    const params = new HttpParams().appendAll({ id: mediaCollection.id, automaticEmbeddingsCreation });
    await firstValueFrom(this.httpClient.put(`${API_PATH}/updateAutomaticEmbeddingsCreation`, null, { params }));
  }

  async removeCollection(mediaCollection: MediaCollection): Promise<void> {
    this.collectionsCache[mediaCollection.speech2TextType].clear();
    const params = new HttpParams().appendAll({ id: mediaCollection.id });
    await firstValueFrom(this.httpClient.delete(`${API_PATH}/remove`, { params }));
  }

  async searchCollections(params: HttpParams): Promise<Paged<MediaCollection> | null> {
    const source = this.httpClient.get<Paged<MediaCollection>>(`${API_PATH}/search`, { params });
    return await this.cancelPrevious("search", source);
  }

  async findAutomaticEmbeddingsCreation(): Promise<MediaCollection[]> {
    const audio = this.collectionsCache.AUDIO.getValue().then((collections) =>
      collections.filter((collection) => collection.automaticEmbeddingsCreation),
    );
    const video = this.collectionsCache.VIDEO.getValue().then((collections) =>
      collections.filter((collection) => collection.automaticEmbeddingsCreation),
    );
    return (await audio).concat(await video);
  }
}
